import { createHeadersFormOptions } from "../dataProvider/fetch";
import { checkAppVersion } from "../dataProvider/utils";
import { handleAuthStatus } from "./authSession";
import { buildApiUrl, extractErrorMessage } from "../dataProvider/requestJson";

/**
 * Message template admin API (system administrators only). The qualifying scores page uses its
 * own governing-body picker (`admin/governing-bodies/{id}/qualifying-score-templates`).
 *
 * Unlike `requestJson`, failures carry `error.status`, because the editor treats a 409
 * (someone else saved first / cannot delete) differently from a 400 validation error.
 */
const BASE = "admin/message-templates";

const STATUS_MESSAGES = {
  401: "Your session has expired. Log in again.",
  403: "Only system administrators can manage message templates.",
};

const query = (params) => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    usp.append(key, String(value));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

const request = async (path, { method = "GET", body } = {}) => {
  const options = { method };
  if (body !== undefined) options.body = JSON.stringify(body);
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: createHeadersFormOptions(options),
  });
  const text = await response.text();
  checkAppVersion(response.headers.get("x-version"));
  handleAuthStatus(response.status);
  if (!response.ok) {
    const error = new Error(
      extractErrorMessage(text, response.status, STATUS_MESSAGES),
    );
    error.status = response.status;
    throw error;
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
};

const post = (path, body) =>
  request(path, { method: "POST", body: body ?? {} });

const enc = encodeURIComponent;
const variantPath = (id, localeId) =>
  `${BASE}/${enc(id)}/variants/${enc(localeId)}`;

/**
 * A logical template (`/{id}`: description, active, which qualifying scores use it) owns one
 * language variant per locale (`/{id}/variants/{localeId}`: subject, body, history).
 */
export const messageTemplateApi = {
  page: ({
    templateName,
    localeId,
    status,
    query: search,
    page,
    size,
    sort,
    isAscending,
  }) =>
    request(
      `${BASE}/page${query({
        templateName,
        localeId,
        status,
        query: search,
        page,
        size,
        sort,
        isAscending,
      })}`,
    ),
  definitions: () => request(`${BASE}/definitions`),
  locales: () => request(`${BASE}/locales`),

  // ---- logical template ----
  get: (id) => request(`${BASE}/${enc(id)}`),
  /** `{templateName, description, variant: {localeId, subject, htmlTemplate, changeNote, variableRequests}}` */
  create: (body) => post(BASE, body),
  update: (id, { description, revision, changeNote }) =>
    post(`${BASE}/${enc(id)}`, { description, revision, changeNote }),
  remove: (id, revision, changeNote) =>
    post(`${BASE}/${enc(id)}/delete`, { revision, changeNote }),
  reactivate: (id, revision, changeNote) =>
    post(`${BASE}/${enc(id)}/reactivate`, { revision, changeNote }),

  // ---- languages ----
  variant: (id, localeId) => request(variantPath(id, localeId)),
  /** `{localeId, subject, htmlTemplate, changeNote, variableRequests}` */
  addVariant: (id, body) => post(`${BASE}/${enc(id)}/variants`, body),
  /** `{subject, htmlTemplate, revision, changeNote, variableRequests}` */
  updateVariant: (id, localeId, body) => post(variantPath(id, localeId), body),
  removeVariant: (id, localeId, revision, changeNote) =>
    post(`${variantPath(id, localeId)}/delete`, { revision, changeNote }),
  reactivateVariant: (id, localeId, revision, changeNote) =>
    post(`${variantPath(id, localeId)}/reactivate`, { revision, changeNote }),
  versions: (id, localeId) => request(`${variantPath(id, localeId)}/versions`),
  version: (id, localeId, revision) =>
    request(`${variantPath(id, localeId)}/versions/${enc(revision)}`),
  restore: (id, localeId, revisionToRestore, currentRevision, changeNote) =>
    post(
      `${variantPath(id, localeId)}/versions/${enc(revisionToRestore)}/restore`,
      { revision: currentRevision, changeNote },
    ),

  // ---- rendering ----
  preview: (body) => post(`${BASE}/preview`, body),
  testEmail: (body) => post(`${BASE}/test-email`, body),

  // ---- requested variables ----
  variableRequests: (status) =>
    request(`${BASE}/variable-requests${query({ status })}`),
  resolveVariableRequest: (id, { status, mappedTo, note }) =>
    post(`${BASE}/variable-requests/${enc(id)}/resolve`, {
      status,
      mappedTo,
      note,
    }),
};

/**
 * The language to open first: the admin's own locale, then en-US, then the first active one,
 * then any. `variants` are `{localeId, active}`.
 */
export const pickDefaultLocale = (variants, preferred) => {
  const list = Array.isArray(variants) ? variants : [];
  const find = (code) =>
    code && list.find((v) => v.localeId?.toLowerCase() === code.toLowerCase());
  const hit =
    find(preferred) || find("en-US") || list.find((v) => v.active) || list[0];
  return hit ? hit.localeId : null;
};

/** `{{ diver.coachEmail }}`, `{{{x}}}` and `diver.coachEmail` all name the same variable. */
export const normalizeTag = (raw) =>
  String(raw || "")
    .trim()
    .replace(/^\{+\s*[#^/&]?\s*/, "")
    .replace(/\s*}+$/, "")
    .trim();

/** A tag an admin may request: dotted identifiers only, so it is always a plain `{{tag}}`. */
export const isValidTag = (tag) =>
  /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/.test(tag || "");

/** Palette groups in catalogue order, each `{group, variables}`; "Message" (own values) first. */
export const groupVariables = (variables, search) => {
  const needle = String(search || "")
    .trim()
    .toLowerCase();
  const groups = new Map();
  (variables || []).forEach((v) => {
    if (
      needle &&
      ![v.name, v.label, v.description, v.group]
        .filter(Boolean)
        .some((text) => text.toLowerCase().includes(needle))
    ) {
      return;
    }
    const key = v.group || "Message";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(v);
  });
  const ordered = [...groups.entries()].map(([group, vars]) => ({
    group,
    variables: vars,
  }));
  ordered.sort((a, b) =>
    a.group === "Message" ? -1 : b.group === "Message" ? 1 : 0,
  );
  return ordered;
};

export const CHANNEL_LABELS = {
  EMAIL: "Email",
  EMAIL_FRAGMENT: "Email fragment",
  PUSH: "Push notification",
  URL_PATH: "Push link path",
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  // LocalDateTime arrives as an ISO string, or as [y, m, d, h, min, s] if the mapper ever
  // switches to numeric timestamps.
  const date = Array.isArray(value)
    ? new Date(
        value[0],
        (value[1] || 1) - 1,
        value[2] || 1,
        value[3] || 0,
        value[4] || 0,
        value[5] || 0,
      )
    : new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

export const auditLine = (at, by) => {
  if (!at && !by) return "—";
  return `${formatDateTime(at)}${by ? ` by ${by}` : ""}`;
};
